import React, { useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import {
  IconInbox,
  IconSearch,
  IconTable,
  SectionCard,
  SplitBar,
  StatusBadge,
} from "./DashboardUi";

const PAGE_SIZE = 8;

/* Theme for react-data-table-component */
const tableStyles = {
  table: { style: { backgroundColor: "transparent" } },
  headRow: {
    style: {
      minHeight: "40px",
      backgroundColor: "#F8FAFC",
      borderBottom: "1px solid #E2E8F0",
    },
  },
  headCells: {
    style: {
      fontSize: "11px",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      color: "#64748B",
    },
  },
  rows: {
    style: {
      fontSize: "13px",
      minHeight: "46px",
      color: "#334155",
      borderBottomColor: "#F1F5F9",
    },
    highlightOnHoverStyle: {
      backgroundColor: "#FFF8EB",
      borderBottomColor: "#F1F5F9",
      outline: "none",
      transitionDuration: "0.15s",
    },
  },
  pagination: {
    style: {
      fontSize: "12px",
      minHeight: "48px",
      color: "#64748B",
      borderTop: "1px solid #E2E8F0",
    },
  },
};

const columns = [
  {
    name: "#",
    selector: (row) => row.sNo,
    sortable: true,
    width: "64px",
  },
  {
    name: "File Name",
    selector: (row) => row.fileName,
    sortable: true,
    grow: 3,
    cell: (row) => (
      <span className="truncate" title={row.fileName}>
        {row.fileName}
      </span>
    ),
  },
  {
    name: "Status",
    selector: (row) => row.label,
    sortable: true,
    width: "130px",
    cell: (row) => <StatusBadge isPass={row.isPass} label={row.label} />,
  },
];

const FILTERS = [
  { key: "all", label: "All" },
  { key: "pass", label: "Passed" },
  { key: "fail", label: "Failed" },
];

const Dashboardtable = ({ data = [] }) => {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  /* Shape raw API data once */
  const sections = useMemo(
    () =>
      (data || []).map((section, idx) => {
        const rows = (section?.article || []).map((item, i) => {
          const isPass = item.count === 1;
          return {
            sNo: i + 1,
            fileName: item.article_title,
            isPass,
            // c_id 9 is the language check: EN / Non EN instead of True / False
            label:
              section.c_id === 9
                ? isPass
                  ? "EN"
                  : "Non EN"
                : isPass
                  ? "True"
                  : "False",
          };
        });
        const passed = rows.filter((r) => r.isPass).length;
        return {
          key: section.c_id ?? idx,
          title: section.title,
          rows,
          passed,
          failed: rows.length - passed,
        };
      }),
    [data],
  );

  const term = search.trim().toLowerCase();

  /* Apply search + status filter to every table */
  const visibleSections = useMemo(
    () =>
      sections.map((s) => ({
        ...s,
        filteredRows: s.rows.filter(
          (r) =>
            (filter === "all" || (filter === "pass" ? r.isPass : !r.isPass)) &&
            (!term || String(r.fileName).toLowerCase().includes(term)),
        ),
      })),
    [sections, filter, term],
  );

  const totals = useMemo(
    () => ({
      all: sections.reduce((n, s) => n + s.rows.length, 0),
      pass: sections.reduce((n, s) => n + s.passed, 0),
      fail: sections.reduce((n, s) => n + s.failed, 0),
    }),
    [sections],
  );

  return (
    <div className="flex w-full flex-col gap-5">
      {/* Toolbar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:w-72">
          <IconSearch
            size={15}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search file name…"
            className="w-full rounded-full border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-slate-600 outline-none transition-colors focus:border-[#FEB13F] focus:bg-white"
          />
        </div>

        <div
          className="inline-flex w-fit rounded-full border border-slate-200 bg-slate-50 p-1"
          role="tablist"
        >
          {FILTERS.map((f) => {
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                role="tab"
                aria-selected={active}
                onClick={() => setFilter(f.key)}
                className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                  active
                    ? "bg-[#154A79] text-white shadow-sm"
                    : "text-slate-500 hover:text-[#154A79]"
                }`}
              >
                {f.label}
                <span
                  className={`ml-1.5 ${
                    active ? "text-white/70" : "text-slate-400"
                  }`}
                >
                  {totals[f.key]}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* One card per check */}
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2 2xl:grid-cols-3">
        {visibleSections.map((section) => {
          const rate = section.rows.length
            ? Math.round((section.passed / section.rows.length) * 100)
            : 0;

          return (
            <SectionCard
              key={section.key}
              icon={<IconTable size={16} />}
              title={section.title}
              subtitle={`${section.rows.length} files`}
              action={
                <span
                  className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                    rate === 100
                      ? "bg-[#EAF7F1] text-[#1B8A5A]"
                      : rate >= 50
                        ? "bg-[#FFF4E1] text-[#B77600]"
                        : "bg-[#FDEDED] text-[#D64545]"
                  }`}
                >
                  {rate}%
                </span>
              }
            >
              <div className="mb-4 space-y-1.5">
                <SplitBar passed={section.passed} failed={section.failed} />
                <div className="flex justify-between text-[11px] font-medium">
                  <span className="text-[#1B8A5A]">
                    {section.passed} passed
                  </span>
                  <span className="text-[#D64545]">
                    {section.failed} failed
                  </span>
                </div>
              </div>

              <div className="overflow-hidden rounded-xl border border-slate-200">
                <DataTable
                  columns={columns}
                  data={section.filteredRows}
                  customStyles={tableStyles}
                  pagination={section.filteredRows.length > PAGE_SIZE}
                  paginationPerPage={PAGE_SIZE}
                  paginationRowsPerPageOptions={[8, 16, 32]}
                  paginationComponentOptions={{ rowsPerPageText: "Rows:" }}
                  highlightOnHover
                  noDataComponent={
                    <div className="flex flex-col items-center gap-2 py-8 text-slate-400">
                      <IconInbox size={20} />
                      <p className="text-xs font-medium">
                        No files match the current filters
                      </p>
                    </div>
                  }
                />
              </div>
            </SectionCard>
          );
        })}
      </div>
    </div>
  );
};

export default Dashboardtable;
